import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Percent, ShieldCheck, AlertTriangle, Loader2, Save, History,
  CheckCircle2, XCircle, Calculator, CreditCard,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import ZeroFeeTransactionsModal from "./ZeroFeeTransactionsModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) =>
  (n === null || n === undefined) ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

const StatusRow = ({ label, ok, value, testId }) => (
  <div className="flex items-center justify-between gap-2 py-1.5" data-testid={testId}>
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="flex items-center gap-1.5 text-xs text-foreground">
      {ok === true && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
      {ok === false && <XCircle className="w-3.5 h-3.5 text-red-400" />}
      {ok === "warn" && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
      {value}
    </span>
  </div>
);

const MODEL_META = {
  merchant_pays: {
    label: "Direct (new)",
    tag: "1% structure",
    desc: "Merchants on the new structure pay Stripe's processing fees themselves — this application fee is 100% pure platform revenue on every payment.",
  },
  platform_pays: {
    label: "Legacy",
    tag: "3.8% structure",
    desc: "Legacy merchants: the platform collects this fee but pays Stripe's processing fee (~2.9% + 30¢) out of it. Your net is the fee minus Stripe's cut.",
  },
};

const AdminPlatformFeePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feeModel, setFeeModel] = useState("merchant_pays");
  const [percent, setPercent] = useState("1");
  const [flat, setFlat] = useState("0.00");
  const [currency, setCurrency] = useState("usd");
  const [example, setExample] = useState("1000");
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState(null);
  const [zeroFeeOpen, setZeroFeeOpen] = useState(false);

  const getAuth = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  const cfgFor = (d, model) => (model === "merchant_pays" ? d?.fee_direct : d?.fee) || {};

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/platform-fee`, getAuth());
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load platform fee settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Sync editor fields whenever data loads or the model tab changes
  useEffect(() => {
    if (!data) return;
    const cfg = cfgFor(data, feeModel);
    setPercent(String(cfg.percent ?? ""));
    setFlat(((cfg.flat_cents ?? 0) / 100).toFixed(2));
    setCurrency(cfg.currency || "usd");
  }, [data, feeModel]);

  const loadAudit = async () => {
    if (auditOpen) { setAuditOpen(false); return; }
    setAuditOpen(true);
    try {
      const res = await axios.get(`${API}/admin/platform-fee/audit?limit=50`, getAuth());
      setAudit(res.data.audit || []);
    } catch { setAudit([]); }
  };

  // Live preview — mirrors backend integer-cent math exactly
  const preview = useMemo(() => {
    const cents = Math.round((parseFloat(example) || 0) * 100);
    const p = parseFloat(percent) || 0;
    const f = Math.round((parseFloat(flat) || 0) * 100);
    if (cents <= 0) return { pct: 0, flat: 0, total: 0, net: 0, estStripe: 0, estNet: 0 };
    const pctCents = Math.round(cents * p / 100);
    const total = Math.min(pctCents + f, cents);
    const estStripeCents = Math.round(cents * 0.029) + 30;
    return {
      pct: pctCents / 100,
      flat: f / 100,
      total: total / 100,
      net: (cents - total) / 100,
      estStripe: estStripeCents / 100,
      estNet: (total - estStripeCents) / 100,
    };
  }, [example, percent, flat]);

  const validate = () => {
    const p = parseFloat(percent);
    const f = parseFloat(flat);
    if (isNaN(p) || p < 0 || p > 20) { toast.error("Percentage fee must be between 0% and 20%"); return false; }
    if (isNaN(f) || f < 0 || f > 10) { toast.error("Flat fee must be between $0.00 and $10.00"); return false; }
    return true;
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/platform-fee`, {
        percent: parseFloat(percent),
        flat_cents: Math.round(parseFloat(flat) * 100),
        currency,
        reason: reason || null,
        confirm: true,
        fee_model: feeModel,
      }, getAuth());
      toast.success(`${MODEL_META[feeModel].label} fee updated — applies to future transactions only`);
      setReason("");
      setConfirmOpen(false);
      const key = feeModel === "merchant_pays" ? "fee_direct" : "fee";
      setData((d) => ({
        ...d,
        [key]: res.data.fee,
        verification: feeModel === "platform_pays"
          ? { ...d.verification, fee_config_status: "saved" }
          : d.verification,
        warnings: feeModel === "platform_pays"
          ? (d.warnings || []).filter((w) => w.code !== "fee_config_default")
          : d.warnings,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update fee");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-border p-6 flex items-center gap-2 text-muted-foreground text-sm" data-testid="platform-fee-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading platform fee settings…
      </div>
    );
  }
  if (!data) return null;

  const v = data.verification || {};
  const wh = v.webhook || {};
  const lastFee = v.last_fee_collected;
  const activeCfg = cfgFor(data, feeModel);
  const dirty = activeCfg && (
    parseFloat(percent) !== activeCfg.percent ||
    Math.round((parseFloat(flat) || 0) * 100) !== activeCfg.flat_cents ||
    currency !== activeCfg.currency
  );
  const isDirect = feeModel === "merchant_pays";
  const meta = MODEL_META[feeModel];

  const auditActionLabel = (a) => {
    if (a.action === "platform_fee_update") return "Legacy fee";
    if (a.action === "platform_fee_direct_update") return "Direct fee (1% structure)";
    if (a.action === "zero_fee_recovered") return "Missed fee recovered";
    if (a.action === "zero_fee_waived") return "Missed fee waived";
    if (a.action === "merchant_override_set") return `Override: ${a.merchant_email || a.merchant_user_id}`;
    return `Override cleared: ${a.merchant_email || a.merchant_user_id}`;
  };

  return (
    <div className="space-y-4" data-testid="platform-fee-panel">
      {/* Warnings */}
      {(data.warnings || []).length > 0 && (
        <div className="space-y-2" data-testid="platform-fee-warnings">
          {data.warnings.map((w) => (
            <div key={w.code} className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-500" data-testid={`fee-warning-${w.code}`}>
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span className="flex-1">{w.message}</span>
              {w.code === "zero_fee_payments" && (
                <button
                  onClick={() => setZeroFeeOpen(true)}
                  className="shrink-0 rounded-md border border-amber-500/40 px-2 py-0.5 text-[11px] font-medium hover:bg-amber-500/10 transition-colors"
                  data-testid="zero-fee-review-btn"
                >
                  Review & fix
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Fee editor */}
        <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="platform-fee-editor">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Percent className="w-4 h-4 text-[#FFCC00]" />
              <h3 className="text-sm font-semibold text-foreground">Platform Processing Fee</h3>
            </div>
            <Badge variant="secondary" className={activeCfg.source === "platform" ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/20" : "bg-amber-500/15 text-amber-500 border-amber-500/20"} data-testid="fee-config-status-badge">
              {activeCfg.source === "platform" ? "Saved" : "Default (unsaved)"}
            </Badge>
          </div>

          {/* Fee model tabs */}
          <div className="flex gap-1 rounded-lg border border-border bg-muted/20 p-1 w-fit" data-testid="fee-model-tabs">
            <button
              onClick={() => setFeeModel("merchant_pays")}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${isDirect ? "bg-emerald-500/15 text-emerald-500 border border-emerald-500/30" : "text-muted-foreground hover:text-foreground border border-transparent"}`}
              data-testid="fee-model-tab-direct"
            >
              Direct (new) · {v.direct_merchants ?? 0} merchant{(v.direct_merchants ?? 0) === 1 ? "" : "s"}
            </button>
            <button
              onClick={() => setFeeModel("platform_pays")}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${!isDirect ? "bg-amber-500/15 text-amber-500 border border-amber-500/30" : "text-muted-foreground hover:text-foreground border border-transparent"}`}
              data-testid="fee-model-tab-legacy"
            >
              Legacy · {v.legacy_merchants ?? 0} merchant{(v.legacy_merchants ?? 0) === 1 ? "" : "s"}
            </button>
          </div>

          <p className="text-xs text-muted-foreground" data-testid="fee-model-description">{meta.desc}</p>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Percentage fee</label>
              <div className="relative">
                <Input type="number" min="0" max="20" step="0.1" value={percent} onChange={(e) => setPercent(e.target.value)} className="pr-7" data-testid="platform-fee-percent-input" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">%</span>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Flat fee</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                <Input type="number" min="0" max="10" step="0.01" value={flat} onChange={(e) => setFlat(e.target.value)} className="pl-6" data-testid="platform-fee-flat-input" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Currency</label>
              <Input value={currency.toUpperCase()} onChange={(e) => setCurrency(e.target.value.toLowerCase())} maxLength={3} data-testid="platform-fee-currency-input" />
            </div>
          </div>

          {/* Live preview */}
          <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2" data-testid="platform-fee-preview">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calculator className="w-3.5 h-3.5" /> Live preview — example payment ({meta.tag})
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">$</span>
              <Input type="number" min="0" value={example} onChange={(e) => setExample(e.target.value)} className="max-w-[120px] h-8" data-testid="platform-fee-example-input" />
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-muted-foreground">Percentage fee ({percent || 0}%)</span>
              <span className="text-right font-mono text-foreground" data-testid="preview-pct-fee">{fmtMoney(preview.pct)}</span>
              <span className="text-muted-foreground">Flat fee</span>
              <span className="text-right font-mono text-foreground" data-testid="preview-flat-fee">{fmtMoney(preview.flat)}</span>
              <span className="text-foreground font-medium">Total Fieldz AI fee</span>
              <span className="text-right font-mono font-semibold text-[#FFCC00]" data-testid="preview-total-fee">{fmtMoney(preview.total)}</span>
              {isDirect ? (
                <>
                  <span className="text-foreground font-medium">Your net revenue</span>
                  <span className="text-right font-mono font-semibold text-emerald-500" data-testid="preview-platform-net">{fmtMoney(preview.total)} (100%)</span>
                </>
              ) : (
                <>
                  <span className="text-muted-foreground">− Est. Stripe cost (2.9% + 30¢)</span>
                  <span className="text-right font-mono text-red-400" data-testid="preview-stripe-cost">−{fmtMoney(preview.estStripe)}</span>
                  <span className="text-foreground font-medium">Your est. net revenue</span>
                  <span className={`text-right font-mono font-semibold ${preview.estNet >= 0 ? "text-emerald-500" : "text-red-400"}`} data-testid="preview-platform-net">{fmtMoney(preview.estNet)}</span>
                </>
              )}
              <span className="text-muted-foreground">Merchant receives</span>
              <span className="text-right font-mono text-foreground" data-testid="preview-net">{fmtMoney(preview.net)}{isDirect ? " − Stripe fees" : ""}</span>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] text-muted-foreground">
              Last updated: {fmtDate(activeCfg.updated_at)}{activeCfg.updated_by ? ` by ${activeCfg.updated_by}` : ""}
            </p>
            <Button size="sm" onClick={() => { if (validate()) setConfirmOpen(true); }} disabled={saving || !dirty} className="gap-1.5" data-testid="platform-fee-save-btn">
              <Save className="w-3.5 h-3.5" /> Save Fee
            </Button>
          </div>
        </div>

        {/* Verification */}
        <div className="rounded-xl border border-border bg-card p-5 space-y-1" data-testid="platform-fee-verification">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-cyan-400" />
            <h3 className="text-sm font-semibold text-foreground">Fee System Verification</h3>
          </div>
          <StatusRow label="Stripe platform account" ok={!!v.platform_account} value={v.platform_account ? `${v.platform_account.id} (${v.key_mode})` : (v.platform_account_error ? "Unreachable" : "Not configured")} testId="verify-platform-account" />
          <StatusRow label="Platform charges enabled" ok={!!v.platform_account?.charges_enabled} value={v.platform_account?.charges_enabled ? "Yes" : "No"} testId="verify-charges-enabled" />
          <StatusRow label="Connected accounts" ok={v.connected_accounts_active > 0} value={`${v.connected_accounts_active} active / ${v.connected_accounts_total} total`} testId="verify-connected-accounts" />
          <StatusRow label="Fee structures" ok={undefined} value={`${v.direct_merchants ?? 0} direct · ${v.legacy_merchants ?? 0} legacy`} testId="verify-fee-structures" />
          <StatusRow label="Direct fee (merchant pays Stripe)" ok={data.fee_direct?.source === "platform" ? true : "warn"} value={`${data.fee_direct?.percent}% + ${fmtMoney((data.fee_direct?.flat_cents || 0) / 100)}${data.fee_direct?.source === "platform" ? "" : " (default)"}`} testId="verify-fee-direct" />
          <StatusRow label="Legacy fee (platform pays Stripe)" ok={data.fee?.source === "platform" ? true : "warn"} value={`${data.fee?.percent}% + ${fmtMoney((data.fee?.flat_cents || 0) / 100)}${data.fee?.source === "platform" ? "" : " (default)"}`} testId="verify-fee-config" />
          <StatusRow label="Webhook secret" ok={wh.secret_configured} value={wh.secret_configured ? "Configured" : "Missing"} testId="verify-webhook-secret" />
          <StatusRow label="Last webhook event" ok={!!(wh.main_last_event_at || wh.connect_last_event_at)} value={fmtDate(wh.main_last_event_at || wh.connect_last_event_at)} testId="verify-webhook-last" />
          <div className="pt-2 mt-2 border-t border-border/50">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <CreditCard className="w-3.5 h-3.5" /> Last platform fee collected
            </div>
            {lastFee ? (
              <p className="text-xs text-foreground" data-testid="verify-last-fee-collected">
                <span className="font-mono font-semibold text-[#FFCC00]">{fmtMoney(lastFee.application_fee)}</span>
                {" "}on a {fmtMoney(lastFee.amount)} payment
                {lastFee.invoice_number ? ` (Invoice ${lastFee.invoice_number})` : ""} — {fmtDate(lastFee.updated_at)}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="verify-last-fee-collected">No platform fee collected yet.</p>
            )}
          </div>
          {(data.merchant_overrides || []).length > 0 && (
            <div className="pt-2 mt-2 border-t border-border/50" data-testid="merchant-overrides-list">
              <p className="text-xs text-muted-foreground mb-1">Per-merchant overrides ({data.merchant_overrides.length})</p>
              {data.merchant_overrides.map((o) => (
                <p key={o.user_id} className="text-[11px] text-foreground">
                  {o.business_name || o.email}: <span className="font-mono">{o.platform_fee_override?.percent}% + {fmtMoney((o.platform_fee_override?.flat_cents || 0) / 100)}</span>
                </p>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Audit history */}
      <div className="rounded-xl border border-border bg-card" data-testid="platform-fee-audit-section">
        <button onClick={loadAudit} className="w-full flex items-center justify-between px-5 py-3 text-sm text-foreground hover:bg-muted/20 transition-colors rounded-xl" data-testid="platform-fee-audit-toggle">
          <span className="flex items-center gap-2"><History className="w-4 h-4 text-muted-foreground" /> Fee Change History</span>
          <span className="text-xs text-muted-foreground">{auditOpen ? "Hide" : "Show"}</span>
        </button>
        {auditOpen && (
          <div className="px-5 pb-4">
            {audit === null ? (
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            ) : audit.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="audit-empty">No fee changes recorded yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-muted/30">
                    <tr className="text-left text-muted-foreground">
                      <th className="font-medium px-3 py-2">Date</th>
                      <th className="font-medium px-3 py-2">Action</th>
                      <th className="font-medium px-3 py-2">Change</th>
                      <th className="font-medium px-3 py-2">By</th>
                      <th className="font-medium px-3 py-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map((a) => (
                      <tr key={a.audit_id} className="border-t border-border/40" data-testid={`audit-row-${a.audit_id}`}>
                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmtDate(a.changed_at)}</td>
                        <td className="px-3 py-2">{auditActionLabel(a)}</td>
                        <td className="px-3 py-2 font-mono whitespace-nowrap">
                          {a.prev_percent}% + {fmtMoney((a.prev_flat_cents || 0) / 100)} → {a.new_percent === null || a.new_percent === undefined ? "default" : `${a.new_percent}% + ${fmtMoney((a.new_flat_cents || 0) / 100)}`}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{a.changed_by_email}</td>
                        <td className="px-3 py-2 text-muted-foreground max-w-[200px] truncate">{a.reason || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Zero-fee payments review & recovery */}
      <ZeroFeeTransactionsModal open={zeroFeeOpen} onOpenChange={setZeroFeeOpen} onChanged={fetchData} />

      {/* Confirmation dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="platform-fee-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Update the {meta.label.toLowerCase()} platform fee?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  New {meta.label} fee: <span className="font-semibold text-foreground">{percent}% + {fmtMoney(parseFloat(flat) || 0)} {currency.toUpperCase()}</span> per successful transaction.
                </p>
                <p>
                  Applies to <strong>{isDirect ? `${v.direct_merchants ?? 0} direct-structure` : `${v.legacy_merchants ?? 0} legacy`} merchant(s)</strong>.
                </p>
                <p className="text-amber-500">
                  This applies to <strong>future transactions only</strong>. Previously completed payments are never modified.
                </p>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Reason for change (optional, stored in audit history)</label>
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="e.g. Annual pricing review" data-testid="platform-fee-reason-input" />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="platform-fee-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); onSave(); }} disabled={saving} data-testid="platform-fee-confirm-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null} Confirm & Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPlatformFeePanel;
